import { useEffect, useState } from "react";
import { Send } from "lucide-react";

import { useSendChannelMessage } from "components/ChatWidget/hooks/use-send-channel-message";
import { useSendChatMessage } from "components/ChatWidget/hooks/use-send-chat-message";

const CHAT_DRAFT_STORAGE_KEY_PREFIX = "kinesin-chat-draft";

// Drafts are keyed per conversation so switching between DMs/channels (or
// navigating away and back) restores the right unsent message for each one.
const getChatDraftStorageKey = ({ chatStaffMemberId, channelId }) => {
  if (channelId) return `${CHAT_DRAFT_STORAGE_KEY_PREFIX}:channel:${channelId}`;
  if (chatStaffMemberId) return `${CHAT_DRAFT_STORAGE_KEY_PREFIX}:dm:${chatStaffMemberId}`;

  return null;
};

export const ChatSendMessageForm = ({ chatStaffMemberId, channelId }) => {
  const draftStorageKey = getChatDraftStorageKey({ chatStaffMemberId, channelId });
  const [input, setInput] = useState(() => (draftStorageKey ? localStorage.getItem(draftStorageKey) || "" : ""));

  const sendDirectMessage = useSendChatMessage(chatStaffMemberId);
  const sendChannelMessage = useSendChannelMessage(channelId);

  const sendMessage = channelId ? sendChannelMessage : sendDirectMessage;
  const isPending = sendMessage.isPending;

  // The form isn't remounted when the active conversation changes, so
  // reload whichever draft belongs to the newly selected conversation.
  useEffect(() => {
    setInput(draftStorageKey ? localStorage.getItem(draftStorageKey) || "" : "");
  }, [draftStorageKey]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);

    if (!draftStorageKey) return;

    if (value) {
      localStorage.setItem(draftStorageKey, value);
    } else {
      localStorage.removeItem(draftStorageKey);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    if (!input.trim() || isPending) return;

    const messageText = input.trim();
    setInput("");
    if (draftStorageKey) localStorage.removeItem(draftStorageKey);
    // Fire-and-forget: the message bubble appears immediately via the
    // hook's optimistic insert and turns red with a retry icon if the
    // send fails, so we don't need to await or surface a toast here.
    sendMessage.mutate(messageText);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      style={{
        borderTop: "1px solid #e5e7eb",
        padding: "12px 16px",
        backgroundColor: "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "0",
          border: "1px solid #d1d5db",
          borderRadius: "8px",
          backgroundColor: "#fff",
          overflow: "hidden",
        }}
      >
        <input
          style={{
            flex: 1,
            border: "none",
            outline: "none",
            fontSize: "0.9375rem",
            backgroundColor: "transparent",
            padding: "12px 16px",
            margin: 0,
            height: "44px",
            boxSizing: "border-box",
          }}
          placeholder="Message..."
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!input.trim() || isPending}
          style={{
            width: "44px",
            height: "44px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: input.trim() ? "#fff" : "#9ca3af",
            backgroundColor: input.trim() ? "#1164a3" : "transparent",
            borderRadius: "0",
            border: "none",
            borderLeft: "1px solid #e5e7eb",
            cursor: input.trim() && !isPending ? "pointer" : "default",
            transition: "all 0.15s",
            flexShrink: 0,
          }}
        >
          <Send style={{ height: "18px", width: "18px" }} />
        </button>
      </div>
    </div>
  );
};
